import { lazy, Suspense } from 'react';
import { GAME_DEFINITIONS } from '@hearth/shared';
import { GrassBlockIcon } from '../app/ChoiceIcons';
import { DiscordMark } from '../app/SignIn';
import { useAuth } from '../auth/AuthProvider';
import { SiteFooter } from '../legal/SiteFooter';
import { navigate } from '../router';
import { Campfire } from '../ui/Campfire';
import { Wordmark } from '../Wordmark';
import { CostCalculator } from './CostCalculator';
import { HEARTH_PER_HOUR, money, SETUP_FEE } from './costs';
import './landing.css';

// three.js is large: the scene loads as its own bundle, after the text is on screen.
const HeroScene = lazy(() => import('./HeroScene'));

/** The front door: the hearth scene, a server's life from create to destroy, the cost calculator, the extras. */
export function Landing() {
  const { status } = useAuth();
  // Signed in: their servers; anyone else: Hearth's sign-in page (Google or Discord).
  const start = () => navigate(status === 'signed-in' ? '/app' : '/signin');

  return (
    <>
      <header className="hero">
        <div className="scene" aria-hidden="true">
          <Suspense fallback={null}>
            <HeroScene />
          </Suspense>
        </div>
        <div className="wrap">
          <div className="hero-inner">
            <div className="hero-copy">
              <Wordmark />
              <h1>
                Game servers built for you. <em>Pay only when you play.</em>
              </h1>
              <p>Invite your friends and only pay for the time you actually play.</p>
              <div className="actions">
                <button className="btn btn-primary" type="button" onClick={start}>{status === 'signed-in' ? 'Your servers' : 'Sign in'}</button>
              </div>
            </div>
          </div>
          <p className="caption">More games joining Hearth soon.</p>
        </div>
      </header>

      <main>
        <section className="band" aria-labelledby="how">
          <div className="wrap">
            <h2 id="how">How it works</h2>
            <ol className="life">
              <li className="stage">
                <span className="stage-num">1 · Create</span>
                <p>Pick a game and choose your settings. Once created, your server starts right away!</p>
                <ul className="games" aria-label="Games">
                  <li><GrassBlockIcon /><b>{GAME_DEFINITIONS['minecraft-java'].displayName}</b></li>
                  <li className="soon"><PortalIcon />Hytale<small>Coming soon</small></li>
                </ul>
                <p className="stage-price"><strong>${SETUP_FEE}</strong> once, to create</p>
              </li>
              <li className="stage-join" aria-hidden="true"><Arrow /></li>
              <li className="stage stage-loop">
                <span className="stage-num">2 · Play, as often as you like</span>
                <h3>Wake it, play, let it sleep</h3>
                <Loop />
                <ul className="loop-key">
                  <li><i className="sw sw-hearth" />Awake: billed by the hour</li>
                  <li><i className="sw sw-asleep" />Asleep: nothing to pay</li>
                </ul>
                <ul className="loop-steps">
                  <li><b>Wake.</b> You, or any friend you've invited, can wake the server to Play.</li>
                  <li><b>Play.</b> Pick up right where you left off last time.</li>
                  <li><b>Sleep.</b> When everyone's gone, it falls asleep on its own. Your world is saved.</li>
                </ul>
                <p className="stage-price"><strong>From {money(HEARTH_PER_HOUR)}</strong> an hour, only while it's awake</p>
              </li>
              <li className="stage-join" aria-hidden="true"><Arrow /></li>
              <li className="stage">
                <span className="stage-num">3 · Destroy</span>
                <p>Destroy it whenever you like. Download your last backup to keep a copy.</p>
                <div className="stage-art"><Campfire state="unlit" height={88} still /></div>
                <p className="stage-price"><strong>Billing stops</strong></p>
              </li>
            </ol>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <h2>Cost Calculator</h2>
            <CostCalculator />
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <h2>Everything else you'd want</h2>
            <div className="feats">
              <div><h3>Frequent backups</h3><p>Your game is backed up as you go, so you can roll back if something goes wrong.</p></div>
              <div className="feat-discord">
                <h3><DiscordMark />Works with your Discord</h3>
                <p>Link a server to your Discord, and anyone invited can type <code>/hearth wake</code> to start playing.</p>
              </div>
              <div><h3>The game as it's meant to be played</h3><p>Hearth doesn't turn settings down. You get the default game, the same one you'd run on your own PC.</p></div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  );
}

function Arrow() {
  return (
    <svg width="28" height="16" viewBox="0 0 28 16">
      <path d="M1 8h22M17 2l7 6-7 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A game still to come: a dormant stone archway, like the Hytale island's portal (original art, decorative). */
function PortalIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 8 8" shapeRendering="crispEdges" aria-hidden="true">
      <path d="M1 8V2h1V1h4v1h1v6H5V3H3v5z" fill="#4a5163" />
      <rect x="3" y="3" width="2" height="5" fill="#2a3040" />
    </svg>
  );
}

/** Wake, play, sleep and round again: the awake arcs ember (billed), the asleep one moonlight (free). */
function Loop() {
  const node = (x: number, y: number, label: string, tone: 'ember' | 'moon') => (
    <g className={`loop-node loop-${tone}`}>
      <circle cx={x} cy={y} r="27" />
      <text x={x} y={y + 5}>{label}</text>
    </g>
  );
  return (
    <svg className="loop" viewBox="0 0 220 200" role="img" aria-label="A loop: wake, play, sleep, and back to wake. Awake time is billed by the hour; asleep is free.">
      <defs>
        <marker id="loop-ember" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" className="loop-head-ember" /></marker>
        <marker id="loop-moon" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" className="loop-head-moon" /></marker>
      </defs>
      <path className="loop-arc loop-ember" d="M142.5 36.9A80 80 0 0 1 189.6 118.4" markerEnd="url(#loop-ember)" />
      <path className="loop-arc loop-ember" d="M157 174.7A80 80 0 0 1 63 174.7" markerEnd="url(#loop-ember)" />
      <path className="loop-arc loop-moon loop-asleep" d="M30.4 118.4A80 80 0 0 1 77.5 36.9" markerEnd="url(#loop-moon)" />
      {node(110, 30, 'Wake', 'ember')}
      {node(179.3, 150, 'Play', 'ember')}
      {node(40.7, 150, 'Sleep', 'moon')}
      <text className="loop-centre" x="110" y="106">Billed by</text>
      <text className="loop-centre" x="110" y="122">the hour</text>
    </svg>
  );
}
